/**
 * Mixpanel Analytics Module
 *
 * Provides a centralized mixpanel instance that can be imported
 * throughout the application.
 */

// eslint-disable-next-line import/default
import mixpanel from 'mixpanel-browser/src/loaders/loader-module-core';

const MIXPANEL_IDENTIFIED_KEY = 'mixpanel_identified_user';
let mixpanelInitialized = false;

/**
 * Check if Mixpanel has been initialized
 */
export function isMixpanelInitialized(): boolean {
  return mixpanelInitialized;
}

/**
 * Track page view with current page information
 */
function trackPageView(): void {
  if (!mixpanelInitialized) {
    return;
  }

  mixpanel.track_pageview();
}

/**
 * Identify the current user with Mixpanel (only once per session)
 */
function identifyUser(): void {
  if (!mixpanelInitialized) {
    return;
  }

  const userDataMeta = document.querySelector<HTMLMetaElement>('meta[name="current-user"]')?.content;

  if (!userDataMeta) {
    // User is not logged in
    // If we have a stored identification, reset Mixpanel and clear it
    if (localStorage.getItem(MIXPANEL_IDENTIFIED_KEY)) {
      mixpanel.reset();
      localStorage.removeItem(MIXPANEL_IDENTIFIED_KEY);
    }
    return;
  }

  try {
    const userData = JSON.parse(userDataMeta) as {
      id: number;
      campminder_id: number | null;
      first_name: string;
      last_name: string;
      email: string;
    };

    // Generate userId matching React Native app logic
    const userId = userData.campminder_id ? String(userData.campminder_id) : `CT${userData.id}`;

    // Check if we've already identified this user in this browser session
    const identifiedUser = localStorage.getItem(MIXPANEL_IDENTIFIED_KEY);
    if (identifiedUser === userId) {
      return; // Already identified
    }

    // Identify the user with Mixpanel
    mixpanel.identify(userId);
    mixpanel.people.set({
      $email: userData.email,
      $first_name: userData.first_name,
      $last_name: userData.last_name,
      colemantown_id: userData.id,
      campminder_id: userData.campminder_id,
      $last_login: new Date().toISOString(),
    });

    // Store that we've identified this user
    localStorage.setItem(MIXPANEL_IDENTIFIED_KEY, userId);
  } catch (error) {
    console.error('Error parsing user data for Mixpanel:', error);
  }
}

/**
 * Initialize Mixpanel with token from meta tag and identify user if logged in
 */
function initializeMixpanel(): void {
  const mixpanelToken = document.querySelector<HTMLMetaElement>('meta[name="mixpanel-token"]')?.content;
  if (mixpanelToken && !mixpanelInitialized) {
    mixpanel.init(mixpanelToken, {
      autocapture: false,
    });

    mixpanelInitialized = true;

    // Turbo will fire this on initial load as well
    // Re-identify on Turbo page loads (in case user logs in) and track page views
    globalThis.document.addEventListener('turbo:load', () => {
      identifyUser();
      trackPageView();
    });
  } else {
    console.warn('Mixpanel token not found in meta tags. Mixpanel is not initialized.');
  }
}

// Initialize on module load
initializeMixpanel();

// Export the mixpanel instance
export { default as mixpanel } from 'mixpanel-browser/src/loaders/loader-module-core';
